import { useEffect, useRef } from 'react'
import type { ReactElement } from 'react'
import { useTranslation } from 'react-i18next'
import { Icon } from './Icon'
import type { ToastVariant } from './useOverlay'

// Auto-dismiss durations per variant (ms)
const DISMISS_MS: Record<ToastVariant, number> = {
  success: 3000,
  info: 4000,
  warn: 5000,
  error: 5000,
}

export interface ToastProps {
  variant: ToastVariant
  text: string
  onClose: () => void
}

export function Toast({ variant, text, onClose }: ToastProps): ReactElement {
  const { t } = useTranslation()
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    timerRef.current = setTimeout(onClose, DISMISS_MS[variant])
    return () => {
      if (timerRef.current !== null) {
        clearTimeout(timerRef.current)
      }
    }
  }, [variant, onClose])

  return (
    <div className={`toast toast--${variant}`} role="alert" aria-live="polite">
      <span style={{ flex: 1 }}>{text}</span>
      <button
        type="button"
        className="toast__close"
        aria-label={t('common.close')}
        onClick={onClose}
      >
        <Icon name="close" size={16} />
      </button>
    </div>
  )
}
